import {
  orderDetailTagsForItem,
  sortItemsForLane,
  sortItemsGlobally,
} from "../utils/stakeholderPriority";

const bucketToLabel = {
  "needs-now": "Needs action",
  moving: "Moving",
  watch: "Watch",
};

const toTitleCase = (value = "") =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getAllItems = (board = []) =>
  board.flatMap((lane) =>
    Object.values(lane.items || {}).flatMap((bucketItems) => bucketItems || [])
  );

const joinTags = (tags = []) => tags.filter(Boolean).slice(0, 3).join(" | ");

const getContactSummary = (item) =>
  item.contact_label ? `Contact: ${item.contact_label}` : "";

const getDadDoctrineEvidence = (item) =>
  [
    item.current_condition_note ? `Condition: ${item.current_condition_note}` : "",
    item.home_proof_signal ? `Home proof: ${item.home_proof_signal}` : "",
    item.weekly_rhythm_signal ? `Rhythm: ${item.weekly_rhythm_signal}` : "",
  ]
    .filter(Boolean)
    .join(" ");

const getLaneIntro = (laneId) => {
  switch (laneId) {
    case "prospects":
      return "Prospect movement is";
    case "dad-partners":
      return "Brotherhood health is";
    case "guide-partners":
      return "Guide Partner activation is";
    case "support-partners":
      return "Supporter trust is";
    case "super-fans":
      return "Super Fan energy is";
    case "advisory-partners":
      return "Advisory grounding is";
    case "operations":
      return "Operational clarity is";
    default:
      return "Lane condition is";
  }
};

const getLaneActionPrefix = (laneId) => {
  switch (laneId) {
    case "prospects":
      return "Contact";
    case "dad-partners":
      return "Encourage";
    case "guide-partners":
      return "Activate";
    case "support-partners":
      return "Care for";
    case "super-fans":
      return "Acknowledge";
    case "advisory-partners":
      return "Prepare";
    case "operations":
      return "Resolve";
    default:
      return "Follow up with";
  }
};

const summarizeItemForReport = (item) => {
  const orderedTags = item.display_detail_tags?.length
    ? item.display_detail_tags
    : orderDetailTagsForItem(item);
  const context = joinTags(orderedTags);
  const contact = getContactSummary(item);

  let summary = item.reason || "No clear reason is recorded yet.";

  switch (item.lane) {
    case "prospects":
      summary = context
        ? `${item.title} is sitting at ${context}. ${item.reason}`
        : `${item.title} is still in the prospect lane. ${item.reason}`;
      break;
    case "dad-partners":
      summary = context
        ? `${item.title} is carrying ${context}. ${item.reason}`
        : `${item.title} is in the Dad Partners lane. ${item.reason}`;
      if (getDadDoctrineEvidence(item)) {
        summary = `${summary} ${getDadDoctrineEvidence(item)}`;
      }
      break;
    case "guide-partners":
      summary = context
        ? `${item.title} is showing ${context}. ${item.reason}`
        : `${item.title} is in the Guide Partners lane. ${item.reason}`;
      break;
    case "support-partners":
      summary = context
        ? `${item.title} currently shows ${context}. ${item.reason}`
        : `${item.title} is in the Support Partners lane. ${item.reason}`;
      break;
    case "super-fans":
      summary = context
        ? `${item.title} currently shows ${context}. ${item.reason}`
        : `${item.title} is in the Super Fans lane. ${item.reason}`;
      break;
    case "advisory-partners":
      summary = context
        ? `${item.title} currently carries ${context}. ${item.reason}`
        : `${item.title} is in the Advisory lane. ${item.reason}`;
      break;
    case "operations":
      summary = context
        ? `${item.title} is being tracked as ${context}. ${item.reason}`
        : `${item.title} is an Operations item. ${item.reason}`;
      break;
    default:
      break;
  }

  return {
    ...item,
    report_summary: summary,
    report_context: context,
    report_contact: contact,
  };
};

const buildLaneInterpretation = (lane, needsActionItems, movingItems, watchItems) => {
  const leadItem = needsActionItems[0] || watchItems[0] || movingItems[0];
  const intro = getLaneIntro(lane.id);

  if (!leadItem) return `${intro} not yet surfaced on the board.`;

  if (needsActionItems.length) {
    return `${intro} under pressure because ${leadItem.report_summary}`;
  }

  if (watchItems.length) {
    return `${intro} worth watching because ${leadItem.report_summary}`;
  }

  return `${intro} moving in the right direction because ${leadItem.report_summary}`;
};

const buildRecommendedMove = (laneId, items) => {
  const leadItem = items[0];
  if (!leadItem) return "Keep this lane visible this week.";
  const prefix = getLaneActionPrefix(laneId);
  const title = leadItem.title || "this item";
  const nextMove = leadItem.next_move || "follow up this week";
  return `${prefix} ${title.toLowerCase() === "financial condition" ? "the financial condition item" : title} and ${nextMove.charAt(0).toLowerCase()}${nextMove.slice(1)}.`;
};

const buildLaneSection = (lane) => {
  const needsActionItems = sortItemsForLane(
    lane.items?.["needs-now"] || [],
    lane.id
  ).map(summarizeItemForReport);
  const movingItems = sortItemsForLane(lane.items?.moving || [], lane.id).map(
    summarizeItemForReport
  );
  const watchItems = sortItemsForLane(lane.items?.watch || [], lane.id).map(
    summarizeItemForReport
  );

  return {
    id: lane.id,
    title: lane.title,
    status: lane.status,
    moving: movingItems.slice(0, 3),
    watch: watchItems.slice(0, 3),
    needsAction: needsActionItems.slice(0, 4),
    recommendedMove: buildRecommendedMove(
      lane.id,
      needsActionItems.length ? needsActionItems : watchItems.length ? watchItems : movingItems
    ),
    interpretation: buildLaneInterpretation(lane, needsActionItems, movingItems, watchItems),
  };
};

export function buildWeeklyOperatingReport({ board, summary }) {
  const allItems = getAllItems(board).map(summarizeItemForReport);
  const needsNow = sortItemsGlobally(
    allItems.filter((item) => item.bucket === "needs-now")
  );
  const movingItems = sortItemsGlobally(
    allItems.filter((item) => item.bucket === "moving")
  );
  const watchItems = sortItemsGlobally(
    allItems.filter((item) => item.bucket === "watch")
  );

  const mostNeglectedLane =
    board
      .slice()
      .sort(
        (left, right) =>
          (right.counts?.watch || 0) + (right.counts?.["needs-now"] || 0) -
          ((left.counts?.watch || 0) + (left.counts?.["needs-now"] || 0))
      )[0] || null;

  const biggestRisk = needsNow[0] || watchItems[0] || null;
  const biggestWin = movingItems[0] || null;
  const financialItem = board
    .find((lane) => lane.id === "operations")
    ?.items?.["needs-now"]?.find((item) => item.title === "Financial condition");

  return {
    generatedLabel: new Date().toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    topLine: {
      needsKevinNow: needsNow.slice(0, 6),
      biggestWin,
      biggestRisk,
      mostNeglectedLane,
      financialStatus: financialItem?.reason || "No financial readout is surfaced yet.",
      overallStatus: summary?.overallStatus || "watch",
    },
    laneSections: board.map(buildLaneSection),
    winsWorthReinforcing: movingItems.slice(0, 5),
    driftToPrevent: watchItems.slice(0, 5),
    nonNegotiables: needsNow.slice(0, 5).map((item) => ({
      id: item.id,
      label: `${item.title}: ${item.next_move || "Follow up this week"}`,
      context: item.report_context,
      contact: item.report_contact,
    })),
    questionsForKevin: [
      "Which warm prospect will you personally call first?",
      "Which dad most needs your encouragement this week?",
      "Which Guide Partner is most needed right now, not in theory?",
      "What operational question still feels too unclear for peace of mind?",
    ],
    meta: {
      counts: summary?.counts || {},
      statuses: board.reduce((acc, lane) => {
        acc[lane.id] = {
          title: lane.title,
          status: toTitleCase(lane.status || "watch"),
          counts: lane.counts || {},
        };
        return acc;
      }, {}),
      bucketLabels: bucketToLabel,
    },
  };
}
